/**
 * Thin wrapper around ElevenLabs' Scribe v2 Realtime WebSocket API. Zero React.
 * Knows nothing about audio capture (it just accepts PCM16 chunks, base64-encodes
 * them into the JSON envelope this protocol wants) or about the `SpeechProvider`
 * interface — `elevenLabsProvider.ts` is the only caller. Deliberately mirrors the
 * shape of `assemblyai/assemblyStreamClient.ts` (send/terminate-or-flush/close)
 * even though the two wire protocols are unrelated — same provider-glue code in
 * `elevenLabsProvider.ts` reads almost line-for-line like `assemblyAIProvider.ts`.
 *
 * Reference: https://elevenlabs.io/docs/api-reference/speech-to-text/v-1-speech-to-text-realtime
 */
import { STT_CONFIG } from '@/config/appConfig';

export type ElevenLabsServerMessageType =
  | 'session_started'
  | 'partial_transcript'
  | 'committed_transcript'
  | 'committed_transcript_with_timestamps'
  | 'committed_transcript_entities'
  // documented error-family message types — see FATAL_SERVER_ERRORS in elevenLabsProvider.ts
  | 'error'
  | 'auth_error'
  | 'quota_exceeded'
  | 'commit_throttled'
  | 'unaccepted_terms'
  | 'rate_limited'
  | 'queue_overflow'
  | 'resource_exhausted'
  | 'session_time_limit_exceeded'
  | 'input_error'
  | 'invalid_request'
  | 'chunk_size_exceeded'
  | 'insufficient_audio_activity'
  | 'transcriber_error';

interface RawServerMessage {
  message_type: ElevenLabsServerMessageType | string;
  text?: string;
  error?: string;
}

export interface ElevenLabsStreamCallbacks {
  onSessionStarted: () => void;
  onCommitted: (text: string) => void;
  /** Any message whose `message_type` isn't `session_started`/`*transcript*` —
   *  the full documented set (`auth_error`, `quota_exceeded`, ...) plus anything
   *  future/undocumented, since this is a foreign API's evolving message set and
   *  the fatal/non-fatal call belongs to the caller (see elevenLabsProvider.ts). */
  onServerMessage: (messageType: string, message?: string) => void;
  onError: (error: unknown) => void;
  onClose: (event: CloseEvent) => void;
}

export interface ElevenLabsStreamHandle {
  send(pcm: ArrayBuffer): void;
  /** Asks the server to commit whatever's currently buffered — there is no server
   *  ack for this (unlike AssemblyAI's Terminate -> Termination), so the caller
   *  always falls back to a fixed finalize timeout regardless. */
  flush(): void;
  /** Hard local teardown: detaches handlers, then closes. Idempotent. */
  close(): void;
}

const MAX_BUFFERED_BYTES = STT_CONFIG.maxBufferedBytes;

function toBase64(pcm: ArrayBuffer): string {
  // btoa over a manually-built binary string — fine at this size (~1600 bytes per
  // chunk, ~50ms of 16kHz/16-bit mono audio sent every ~50ms); no need for a
  // chunked base64 encoder here.
  const bytes = new Uint8Array(pcm);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export function connectElevenLabsStream(token: string, callbacks: ElevenLabsStreamCallbacks): ElevenLabsStreamHandle {
  const params = new URLSearchParams({
    token,
    model_id: 'scribe_v2_realtime',
    audio_format: 'pcm_16000',
    // VAD, not manual: we want ElevenLabs' own end-of-speech detection driving
    // when a segment commits, the same way AssemblyAI's own turn detection does —
    // that's what makes the two providers actually comparable for a benchmark,
    // rather than one running its native segmentation and the other running a
    // single manually-triggered commit.
    commit_strategy: 'vad',
  });

  const ws = new WebSocket(`wss://api.elevenlabs.io/v1/speech-to-text/realtime?${params.toString()}`);

  ws.onmessage = (event: MessageEvent<string>) => {
    let message: RawServerMessage;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }
    switch (message.message_type) {
      case 'session_started':
        callbacks.onSessionStarted();
        break;
      case 'committed_transcript':
        callbacks.onCommitted(message.text ?? '');
        break;
      case 'partial_transcript':
      case 'committed_transcript_with_timestamps':
      case 'committed_transcript_entities':
        // Partials are never surfaced (see useSpeechInput's onPartial no-op) and
        // the timestamp/entity variants carry no data this app uses.
        break;
      default:
        callbacks.onServerMessage(message.message_type, message.error);
    }
  };
  ws.onerror = (event) => callbacks.onError(event);
  ws.onclose = (event) => callbacks.onClose(event);

  return {
    send(pcm: ArrayBuffer): void {
      if (ws.readyState !== WebSocket.OPEN) return;
      if (ws.bufferedAmount > MAX_BUFFERED_BYTES) return;
      ws.send(JSON.stringify({
        message_type: 'input_audio_chunk',
        audio_base_64: toBase64(pcm),
        commit: false,
        sample_rate: 16000,
      }));
    },
    flush(): void {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          message_type: 'input_audio_chunk',
          audio_base_64: '',
          commit: true,
          sample_rate: 16000,
        }));
      }
    },
    close(): void {
      // Detach handlers before closing: an onclose firing after the caller has
      // already torn down its own state would otherwise setState on a session
      // that no longer exists. Mirrors assemblyStreamClient.ts's close().
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    },
  };
}
